/*
 * Hotspot slideshow.
 * Wraps the theme's <slideshow-component> (global.js) and adds:
 * - slide labels (theme blocks do not know their index)
 * - smooth container height transition to the active slide
 * - desktop tooltips (hover / focus / click, Esc, edge flipping)
 * - mobile bottom sheet (non-modal: other hotspots stay tappable, content swaps in place)
 * - autoplay pause while a tooltip or sheet is open
 * - theme editor block selection support
 */
if (!customElements.get('hotspot-slideshow')) {
  class HotspotSlideshow extends HTMLElement {
    connectedCallback() {
      // Wait a microtask so the inner <slideshow-component> is upgraded first (theme editor reloads).
      Promise.resolve().then(() => this.init());
    }

    disconnectedCallback() {
      this.teardown();
    }

    init() {
      if (this.initialized || !this.isConnected) return;
      this.initialized = true;

      this.slideshow = this.querySelector('slideshow-component');
      this.slider = this.querySelector('[id^="Slider-"]');
      if (!this.slideshow || !this.slider) return;

      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.active = null;
      this.pinned = false;
      this.holdAutoplay = false;
      this.closeTimer = null;
      this.sheetOpen = false;

      this.slides = Array.from(this.slider.querySelectorAll('[id^="Slide-"]'));
      this.labelSlides();
      this.patchAutoplay();

      this.abort = new AbortController();
      const opts = { signal: this.abort.signal };

      this.querySelectorAll('[data-hotspot]').forEach((hotspot) => {
        const marker = hotspot.querySelector('.hotspot__marker');
        hotspot.addEventListener('mouseenter', () => this.onHover(hotspot), opts);
        hotspot.addEventListener('mouseleave', () => this.onLeave(hotspot), opts);
        hotspot.addEventListener('focusin', (event) => this.onFocusIn(hotspot, event), opts);
        hotspot.addEventListener('focusout', (event) => this.onFocusOut(hotspot, event), opts);
        marker.addEventListener('click', () => this.onMarkerClick(hotspot), opts);
      });

      this.querySelectorAll('[data-hotspot-close]').forEach((el) =>
        el.addEventListener('click', () => this.closeSheet(), opts)
      );

      document.addEventListener('keydown', (event) => this.onKeydown(event), opts);
      document.addEventListener('click', (event) => this.onDocumentClick(event), opts);
      this.slideshow.addEventListener('slideChanged', () => this.onSlideChanged(), opts);
      this.mobileQuery.addEventListener('change', () => this.closeAll(), opts);

      // Theme editor
      this.addEventListener('shopify:block:select', (event) => this.onBlockSelect(event), opts);
      this.addEventListener('shopify:block:deselect', () => this.closeAll(), opts);

      // Height: follow the active slide (image load, resize, text wrapping)
      this.resizeObserver = new ResizeObserver((entries) => {
        const slide = this.getActiveSlide();
        if (entries.some((entry) => entry.target === slide)) this.syncHeight(false);
      });
      this.slides.forEach((slide) => this.resizeObserver.observe(slide));

      // Mobile sheet is non-modal and the page can scroll: close it once the slideshow leaves the viewport
      this.visibilityObserver = new IntersectionObserver((entries) => {
        if (this.sheetOpen && !entries[0].isIntersecting) this.closeSheet(false);
      });
      this.visibilityObserver.observe(this.slider);

      this.updateMarkerFocusability();
      this.syncHeight(false);
    }

    teardown() {
      if (!this.initialized) return;
      this.initialized = false;
      this.abort?.abort();
      this.resizeObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      clearTimeout(this.closeTimer);
      clearTimeout(this.heightTimer);
    }

    /* Slides */

    labelSlides() {
      const of = this.dataset.of || 'of';
      this.slides.forEach((slide, index) => {
        slide.setAttribute('aria-label', `${index + 1} ${of} ${this.slides.length}`);
      });
    }

    getActiveSlide() {
      const items = this.slideshow.sliderItemsToShow;
      const page = this.slideshow.currentPage;
      return (items && page && items[page - 1]) || this.slides[0];
    }

    syncHeight(animate) {
      const slide = this.getActiveSlide();
      if (!slide) return;
      const height = slide.offsetHeight;
      if (!height) return;
      if (animate) {
        this.slider.classList.add('is-height-animating');
        clearTimeout(this.heightTimer);
        this.heightTimer = setTimeout(() => this.slider.classList.remove('is-height-animating'), 500);
      }
      this.slider.style.height = `${height}px`;
    }

    onSlideChanged() {
      this.closeAll();
      this.syncHeight(true);
      this.updateMarkerFocusability();
    }

    // Markers in off-screen slides must not be reachable with the keyboard
    updateMarkerFocusability() {
      const current = this.getActiveSlide();
      this.slides.forEach((slide) => {
        slide.querySelectorAll('.hotspot__marker').forEach((marker) => {
          if (slide === current) marker.removeAttribute('tabindex');
          else marker.setAttribute('tabindex', '-1');
        });
      });
    }

    /* Autoplay: block resume while a tooltip or sheet is open */

    patchAutoplay() {
      const slideshow = this.slideshow;
      if (typeof slideshow.play !== 'function') return;
      const originalPlay = slideshow.play.bind(slideshow);
      slideshow.play = () => {
        if (this.holdAutoplay) return;
        originalPlay();
      };
    }

    holdAutoplayOn() {
      this.holdAutoplay = true;
      if (typeof this.slideshow.pause === 'function' && this.slider.dataset.autoplay === 'true') {
        this.slideshow.pause();
      }
    }

    holdAutoplayOff() {
      this.holdAutoplay = false;
      // autoplayButtonIsSetToPlay is false when the user paused the slideshow manually
      if (this.slider.dataset.autoplay === 'true' && this.slideshow.autoplayButtonIsSetToPlay) {
        this.slideshow.play();
      }
    }

    /* Events */

    onHover(hotspot) {
      if (this.mobileQuery.matches) return;
      clearTimeout(this.closeTimer);
      this.open(hotspot, false);
    }

    onLeave(hotspot) {
      if (this.mobileQuery.matches || this.active !== hotspot || this.pinned) return;
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => {
        if (!this.pinned && !hotspot.contains(document.activeElement)) this.close(hotspot);
      }, 150);
    }

    onFocusIn(hotspot, event) {
      if (this.mobileQuery.matches) return;
      if (event.target.classList.contains('hotspot__marker') && this.active !== hotspot) {
        this.open(hotspot, false);
      }
    }

    onFocusOut(hotspot, event) {
      if (this.mobileQuery.matches || this.active !== hotspot) return;
      if (event.relatedTarget && !hotspot.contains(event.relatedTarget)) this.close(hotspot);
    }

    onMarkerClick(hotspot) {
      if (this.mobileQuery.matches) {
        if (this.sheetOpen && this.active === hotspot) this.closeSheet(false);
        else this.openSheet(hotspot);
        return;
      }
      if (this.active === hotspot && this.pinned) {
        this.close(hotspot);
      } else {
        this.open(hotspot, true);
      }
    }

    onDocumentClick(event) {
      if (this.mobileQuery.matches) {
        // Tap outside the sheet and outside any hotspot closes it; taps on other hotspots swap the content
        if (!this.sheetOpen) return;
        const target = event.target;
        if (this.sheet.contains(target) || target.closest('[data-hotspot]')) return;
        this.closeSheet(false);
        return;
      }
      if (!this.active) return;
      if (!this.active.contains(event.target)) this.close(this.active);
    }

    onKeydown(event) {
      if (event.key !== 'Escape') return;
      if (this.sheetOpen) {
        event.preventDefault();
        this.closeSheet();
      } else if (this.active) {
        const hotspot = this.active;
        const hadFocus = hotspot.contains(document.activeElement);
        this.close(hotspot);
        if (hadFocus) hotspot.querySelector('.hotspot__marker').focus();
      }
    }

    /* Desktop tooltip */

    open(hotspot, pin) {
      if (this.active && this.active !== hotspot) this.close(this.active);
      this.active = hotspot;
      this.pinned = pin || (this.active === hotspot && this.pinned);
      hotspot.classList.add('is-open');
      const marker = hotspot.querySelector('.hotspot__marker');
      marker.setAttribute('aria-expanded', 'true');
      marker.setAttribute('aria-controls', hotspot.querySelector('.hotspot__tooltip').id);
      this.positionTooltip(hotspot);
      this.holdAutoplayOn();
    }

    close(hotspot) {
      if (!hotspot) return;
      hotspot.classList.remove('is-open');
      hotspot.querySelector('.hotspot__marker').setAttribute('aria-expanded', 'false');
      if (this.active === hotspot) {
        this.active = null;
        this.pinned = false;
        this.holdAutoplayOff();
      }
    }

    closeAll() {
      clearTimeout(this.closeTimer);
      if (this.sheetOpen) this.closeSheet(false);
      if (this.active) this.close(this.active);
    }

    // Keep the tooltip inside the slide frame: flip above/below and shift horizontally
    positionTooltip(hotspot) {
      const tooltip = hotspot.querySelector('.hotspot__tooltip');
      const frame = hotspot.closest('.hotspot-slide__frame');
      if (!tooltip || !frame) return;

      hotspot.classList.remove('hotspot--below');
      tooltip.style.setProperty('--tip-shift', '0px');

      const frameRect = frame.getBoundingClientRect();
      const marker = hotspot.querySelector('.hotspot__marker').getBoundingClientRect();
      const tipWidth = tooltip.offsetWidth;
      const tipHeight = tooltip.offsetHeight;
      const gap = 8 + marker.height / 2;
      const margin = 8;

      const spaceAbove = marker.top + marker.height / 2 - frameRect.top - gap;
      const spaceBelow = frameRect.bottom - (marker.top + marker.height / 2) - gap;
      if (spaceAbove < tipHeight + margin && spaceBelow > spaceAbove) {
        hotspot.classList.add('hotspot--below');
      }

      const centerX = marker.left + marker.width / 2;
      const minLeft = frameRect.left + margin;
      const maxLeft = frameRect.right - margin - tipWidth;
      const desiredLeft = centerX - tipWidth / 2;
      const clamped = Math.max(minLeft, Math.min(desiredLeft, Math.max(minLeft, maxLeft)));
      tooltip.style.setProperty('--tip-shift', `${Math.round(clamped - desiredLeft)}px`);
    }

    /* Mobile bottom sheet */

    get sheet() {
      return this.querySelector('.hotspot-sheet');
    }

    openSheet(hotspot) {
      const sheet = this.sheet;
      const card = hotspot.querySelector('.hotspot__card');
      if (!sheet || !card) return;

      const wasOpen = this.sheetOpen;
      if (this.active && this.active !== hotspot) {
        const previous = this.active.querySelector('.hotspot__marker');
        this.active.classList.remove('is-open');
        previous.setAttribute('aria-expanded', 'false');
        previous.setAttribute('aria-controls', this.active.querySelector('.hotspot__tooltip').id);
      }

      const panel = sheet.querySelector('.hotspot-sheet__panel');
      const content = sheet.querySelector('.hotspot-sheet__content');
      const marker = hotspot.querySelector('.hotspot__marker');

      content.replaceChildren(card.cloneNode(true));
      panel.setAttribute('aria-label', marker.getAttribute('aria-label'));
      panel.scrollTop = 0;

      this.sheetOpener = marker;
      this.active = hotspot;
      hotspot.classList.add('is-open');
      marker.setAttribute('aria-expanded', 'true');
      marker.setAttribute('aria-controls', sheet.id);

      if (!wasOpen) {
        sheet.classList.add('is-open');
        this.sheetOpen = true;
        this.holdAutoplayOn();
      }
    }

    closeSheet(restoreFocus = true) {
      const sheet = this.sheet;
      if (!sheet || !this.sheetOpen) return;
      const focusWasInside = sheet.contains(document.activeElement);
      sheet.classList.remove('is-open');
      this.sheetOpen = false;
      const opener = this.sheetOpener;
      if (opener) {
        opener.setAttribute('aria-expanded', 'false');
        opener.setAttribute('aria-controls', opener.closest('[data-hotspot]').querySelector('.hotspot__tooltip').id);
      }
      this.active?.classList.remove('is-open');
      if (opener && (restoreFocus || focusWasInside)) opener.focus({ preventScroll: true });
      this.sheetOpener = null;
      this.active = null;
      this.pinned = false;
      this.holdAutoplayOff();
    }

    /* Theme editor */

    onBlockSelect(event) {
      const target = event.target;
      const slide = target.closest('[id^="Slide-"]');
      if (!slide || !this.slider.contains(slide)) return;

      const hotspot = target.matches('[data-hotspot]') ? target : null;
      const isCurrent = this.getActiveSlide() === slide;

      if (!isCurrent) {
        this.closeAll();
        this.slider.scrollTo({ left: slide.offsetLeft });
      }
      if (hotspot) {
        setTimeout(() => {
          if (this.mobileQuery.matches) this.openSheet(hotspot);
          else this.open(hotspot, true);
        }, isCurrent ? 0 : 450);
      }
    }
  }

  customElements.define('hotspot-slideshow', HotspotSlideshow);
}
